import OpenAI from 'openai';

export interface OpenAIConfig {
  apiKey: string;
  organization?: string;
  baseURL?: string;
}

export interface ContentGenerationRequest {
  type: 'newsletter' | 'article' | 'announcement' | 'poll' | 'event' | 'media' | 'series' | 'image-analysis' | 'image-generation';
  topic?: string;
  tone?: 'professional' | 'casual' | 'friendly' | 'authoritative';
  length?: 'short' | 'medium' | 'long';
  audience?: string;
  keywords?: string[];
  template?: string;
  context?: string;
  // Image-specific properties
  imageUrl?: string;
  prompt?: string;
  style?: 'realistic' | 'artistic' | 'cartoon' | 'sketch' | 'oil-painting' | 'watercolor';
  size?: '512x512' | '1024x1024' | '1024x768' | '768x1024';
  quality?: 'standard' | 'high' | 'ultra';
  count?: number;
  maxTokens?: number;
}

export interface ContentGenerationResponse {
  title: string;
  content: string;
  excerpt: string;
  tags: string[];
  suggestions: string[];
  confidence: number;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface WritingSuggestion {
  type: 'grammar' | 'style' | 'clarity' | 'engagement';
  suggestion: string;
  original: string;
  improved: string;
  confidence: number;
}

export interface SubjectLineOptions {
  options: string[];
  analysis: {
    best: string;
    reasoning: string;
    openRatePrediction: number;
  };
}

class OpenAIService {
  private client: OpenAI | null = null;
  private isConfigured: boolean = false;

  constructor() {
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    
    if (!apiKey || apiKey === 'sk-your_openai_api_key_here') {
      console.warn('OpenAI API key not configured. Using mock responses.');
      this.isConfigured = false;
      return;
    }

    try {
      this.client = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true, // Note: In production, this should be server-side
      });
      this.isConfigured = true;
      console.log('OpenAI service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize OpenAI service:', error);
      this.isConfigured = false;
    }
  }


  async improveContent(content: string, improvements: string[]): Promise<string> {
    if (!this.isConfigured || !this.client) {
      return this.mockImproveContent(content, improvements);
    }

    try {
      const prompt = `Please improve the following content based on these specific improvements: ${improvements.join(', ')}. Return only the improved content without any explanations.

Content to improve:
${content}`;

      const response = await this.client.chat.completions.create({
        model: 'gpt-4',
        messages: [
          {
            role: 'system',
            content: 'You are an expert content editor. Improve content based on specific requirements while maintaining the original voice and style.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 1500,
        temperature: 0.3
      });

      return response.choices[0]?.message?.content || content;
    } catch (error) {
      console.error('Error improving content with OpenAI:', error);
      return this.mockImproveContent(content, improvements);
    }
  }

  async generateWritingSuggestions(content: string): Promise<WritingSuggestion[]> {
    if (!this.isConfigured || !this.client) {
      return this.mockGenerateWritingSuggestions(content);
    }

    try {
      const prompt = `Analyze the following content and provide specific writing suggestions for grammar, style, clarity, and engagement. Return suggestions in JSON format with this structure:
[
  {
    "type": "grammar|style|clarity|engagement",
    "suggestion": "Brief description of the suggestion",
    "original": "Original text",
    "improved": "Improved text",
    "confidence": 0.85
  }
]

Content to analyze:
${content}`;

      const response = await this.client.chat.completions.create({
        model: 'gpt-4',
        messages: [
          {
            role: 'system',
            content: 'You are an expert writing coach. Provide specific, actionable suggestions to improve content quality.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 1000,
        temperature: 0.3
      });

      const suggestionsText = response.choices[0]?.message?.content || '[]';
      try {
        return JSON.parse(suggestionsText);
      } catch {
        return this.mockGenerateWritingSuggestions(content);
      }
    } catch (error) {
      console.error('Error generating writing suggestions with OpenAI:', error);
      return this.mockGenerateWritingSuggestions(content);
    }
  }

  async generateSubjectLines(content: string, count: number = 5): Promise<SubjectLineOptions> {
    if (!this.isConfigured || !this.client) {
      return this.mockGenerateSubjectLines(content, count);
    }

    try {
      const prompt = `Generate ${count} compelling email subject lines for the following content. Analyze which would likely get the highest open rate and explain why. Return in JSON format:
{
  "options": ["subject line 1", "subject line 2", ...],
  "analysis": {
    "best": "best subject line",
    "reasoning": "explanation of why this is best",
    "openRatePrediction": 0.85
  }
}

Content:
${content}`;

      const response = await this.client.chat.completions.create({
        model: 'gpt-4',
        messages: [
          {
            role: 'system',
            content: 'You are an expert email marketer. Create compelling subject lines that maximize open rates while being honest and engaging.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 500,
        temperature: 0.8
      });

      const subjectLinesText = response.choices[0]?.message?.content || '{}';
      try {
        return JSON.parse(subjectLinesText);
      } catch {
        return this.mockGenerateSubjectLines(content, count);
      }
    } catch (error) {
      console.error('Error generating subject lines with OpenAI:', error);
      return this.mockGenerateSubjectLines(content, count);
    }
  }

  async transcribeAudio(audioBlob: Blob): Promise<string> {
    if (!this.isConfigured || !this.client) {
      return this.mockTranscribeAudio();
    }

    try {
      // Note: This would require the audio to be converted to a format OpenAI accepts
      // For now, we'll use a mock implementation
      console.log('Audio transcription not yet implemented with OpenAI API');
      return this.mockTranscribeAudio();
    } catch (error) {
      console.error('Error transcribing audio with OpenAI:', error);
      return this.mockTranscribeAudio();
    }
  }

  // Image Analysis using GPT-4 Vision
  async analyzeImage(imageUrl: string, prompt: string, maxTokens: number = 1000): Promise<ContentGenerationResponse> {
    if (!this.isConfigured || !this.client) {
      return this.mockAnalyzeImage(imageUrl, prompt);
    }

    try {
      const response = await this.client.chat.completions.create({
        model: 'gpt-4-vision-preview',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: prompt
              },
              {
                type: 'image_url',
                image_url: {
                  url: imageUrl,
                  detail: 'high'
                }
              }
            ]
          }
        ],
        max_tokens: maxTokens,
        temperature: 0.3
      });

      const content = response.choices[0]?.message?.content || '';
      
      return {
        title: 'Image Analysis',
        content: content,
        excerpt: content.substring(0, 150) + '...',
        tags: ['image-analysis', 'ai-generated'],
        suggestions: ['Review the analysis', 'Apply suggested tags', 'Use for content creation'],
        confidence: 0.9,
        usage: response.usage
      };
    } catch (error) {
      console.error('Error analyzing image with OpenAI:', error);
      return this.mockAnalyzeImage(imageUrl, prompt);
    }
  }

  // Image Generation using DALL-E
  async generateImage(prompt: string, style: string = 'realistic', size: string = '1024x1024', quality: string = 'high'): Promise<ContentGenerationResponse> {
    if (!this.isConfigured || !this.client) {
      return this.mockGenerateImage(prompt, style, size);
    }

    try {
      const enhancedPrompt = this.enhanceImagePrompt(prompt, style);
      
      const response = await this.client.images.generate({
        model: 'dall-e-3',
        prompt: enhancedPrompt,
        size: size as any,
        quality: quality as any,
        n: 1,
        response_format: 'url'
      });

      const imageUrl = response.data?.[0]?.url || '';
      
      return {
        title: `Generated Image: ${prompt}`,
        content: `<img src="${imageUrl}" alt="${prompt}" style="max-width: 100%; height: auto;" />`,
        excerpt: `AI-generated image based on: ${prompt}`,
        tags: ['ai-generated', 'dall-e', style, 'image'],
        suggestions: ['Download the image', 'Use in your content', 'Modify the prompt for variations'],
        confidence: 0.9
      };
    } catch (error) {
      console.error('Error generating image with OpenAI:', error);
      return this.mockGenerateImage(prompt, style, size);
    }
  }

  // Enhanced generateContent method to handle image operations
  async generateContent(request: ContentGenerationRequest): Promise<ContentGenerationResponse> {
    if (!this.isConfigured || !this.client) {
      return this.generateMockContent(request);
    }

    // Handle image analysis
    if (request.type === 'image-analysis' && request.imageUrl && request.prompt) {
      return this.analyzeImage(request.imageUrl, request.prompt, request.maxTokens || 1000);
    }

    // Handle image generation
    if (request.type === 'image-generation' && request.prompt) {
      return this.generateImage(
        request.prompt, 
        request.style || 'realistic', 
        request.size || '1024x1024', 
        request.quality || 'high'
      );
    }

    // Handle regular content generation
    try {
      const prompt = this.buildContentPrompt(request);
      
      const response = await this.client.chat.completions.create({
        model: 'gpt-4',
        messages: [
          {
            role: 'system',
            content: 'You are an expert content writer specializing in newsletter and content marketing. Generate high-quality, engaging content that matches the specified requirements.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: request.maxTokens || 2000,
        temperature: 0.7,
        presence_penalty: 0.1,
        frequency_penalty: 0.1
      });

      const content = response.choices[0]?.message?.content || '';
      const parsed = this.parseContentResponse(content, request.type);

      return {
        ...parsed,
        usage: response.usage
      };
    } catch (error) {
      console.error('Error generating content with OpenAI:', error);
      // Fallback to mock content
      return this.generateMockContent(request);
    }
  }

  // Helper method to enhance image prompts
  private enhanceImagePrompt(prompt: string, style: string): string {
    let enhancedPrompt = prompt;
    
    // Add style-specific enhancements
    switch (style) {
      case 'realistic':
        enhancedPrompt += ', photorealistic, high quality, detailed, professional photography';
        break;
      case 'artistic':
        enhancedPrompt += ', artistic, creative, expressive, unique style';
        break;
      case 'cartoon':
        enhancedPrompt += ', cartoon style, colorful, fun, animated';
        break;
      case 'sketch':
        enhancedPrompt += ', pencil sketch, hand-drawn, artistic, line art';
        break;
      case 'oil-painting':
        enhancedPrompt += ', oil painting style, classical art, textured, brushstrokes';
        break;
      case 'watercolor':
        enhancedPrompt += ', watercolor painting, soft colors, artistic, flowing';
        break;
      default:
        enhancedPrompt += ', high quality, professional';
    }
    
    return enhancedPrompt;
  }

  private buildContentPrompt(request: ContentGenerationRequest): string {
    const { type, topic, tone, length, audience, keywords, context } = request;
    
    let prompt = `Create a ${type} about ${topic || 'a relevant topic'}. `;
    
    if (tone) {
      prompt += `Use a ${tone} tone. `;
    }
    
    if (length) {
      const lengthMap = {
        short: '200-300 words',
        medium: '400-600 words',
        long: '800-1200 words'
      };
      prompt += `Keep it ${lengthMap[length]}. `;
    }
    
    if (audience) {
      prompt += `Target audience: ${audience}. `;
    }
    
    if (keywords && keywords.length > 0) {
      prompt += `Include these keywords naturally: ${keywords.join(', ')}. `;
    }
    
    if (context) {
      prompt += `Context: ${context}. `;
    }
    
    prompt += `\n\nReturn the response in this exact JSON format:
{
  "title": "Compelling title",
  "content": "Full HTML content with proper formatting",
  "excerpt": "Brief summary (1-2 sentences)",
  "tags": ["tag1", "tag2", "tag3"],
  "suggestions": ["suggestion1", "suggestion2", "suggestion3"]
}`;

    return prompt;
  }

  private parseContentResponse(content: string, type: string): Omit<ContentGenerationResponse, 'usage'> {
    try {
      const parsed = JSON.parse(content);
      return {
        title: parsed.title || `Generated ${type}`,
        content: parsed.content || content,
        excerpt: parsed.excerpt || 'Generated content excerpt',
        tags: parsed.tags || [type, 'ai-generated'],
        suggestions: parsed.suggestions || ['Add more examples', 'Include statistics', 'Add call-to-action'],
        confidence: 0.9
      };
    } catch (error) {
      // If JSON parsing fails, treat the entire response as content
      return {
        title: `Generated ${type}`,
        content: content,
        excerpt: content.substring(0, 150) + '...',
        tags: [type, 'ai-generated'],
        suggestions: ['Add more examples', 'Include statistics', 'Add call-to-action'],
        confidence: 0.7
      };
    }
  }

  // Mock implementations for fallback
  private generateMockContent(request: ContentGenerationRequest): ContentGenerationResponse {
    const { type, topic, tone = 'professional', length = 'medium' } = request;
    
    return {
      title: `Mock ${type}: ${topic || 'Sample Topic'}`,
      content: `<h1>Mock ${type} Content</h1><p>This is mock content for ${type} about ${topic || 'your topic'}.</p>`,
      excerpt: `Mock excerpt for ${type}`,
      tags: [type, 'mock', 'sample'],
      suggestions: ['This is mock content - replace with real AI generation'],
      confidence: 0.1
    };
  }

  private mockImproveContent(content: string, improvements: string[]): string {
    return `
      <div style="background: #f0f9ff; border: 1px solid #0ea5e9; border-radius: 8px; padding: 16px; margin: 16px 0;">
        <h3>AI-Improved Content (Mock)</h3>
        <p>Your content has been enhanced with the following improvements:</p>
        <ul>
          ${improvements.map(improvement => `<li>${improvement}</li>`).join('')}
        </ul>
      </div>
      ${content}
    `;
  }

  private mockGenerateWritingSuggestions(content: string): WritingSuggestion[] {
    return [
      {
        type: 'grammar',
        suggestion: 'Consider using more active voice',
        original: 'The content was written by the team',
        improved: 'The team wrote the content',
        confidence: 0.8
      },
      {
        type: 'style',
        suggestion: 'Add more specific examples',
        original: 'Many people find this helpful',
        improved: 'Over 80% of users find this feature helpful',
        confidence: 0.7
      }
    ];
  }

  private mockGenerateSubjectLines(content: string, count: number): SubjectLineOptions {
    return {
      options: [
        'Weekly Update: Important News',
        'Don\'t Miss This Week\'s Highlights',
        'Your Weekly Newsletter is Here',
        'What\'s New This Week?',
        'Weekly Roundup: Must-Read Content'
      ],
      analysis: {
        best: 'Don\'t Miss This Week\'s Highlights',
        reasoning: 'Creates urgency and curiosity',
        openRatePrediction: 0.75
      }
    };
  }

  private mockTranscribeAudio(): string {
    return `
      <div style="background: #f3f4f6; border-radius: 8px; padding: 16px; margin: 16px 0;">
        <h3>Transcribed Content (Mock)</h3>
        <p><em>This is a mock transcription. In production, this would be the actual transcribed text from your audio recording.</em></p>
        <p>You can edit this content as needed to match your intended message and style.</p>
      </div>
    `;
  }

  private mockAnalyzeImage(imageUrl: string, prompt: string): ContentGenerationResponse {
    return {
      title: 'Image Analysis (Mock)',
      content: `
        <div style="background: #f0f9ff; border: 1px solid #0ea5e9; border-radius: 8px; padding: 16px; margin: 16px 0;">
          <h3>AI Image Analysis (Mock)</h3>
          <p><strong>Image URL:</strong> ${imageUrl}</p>
          <p><strong>Analysis Prompt:</strong> ${prompt}</p>
          <div style="background: white; padding: 12px; border-radius: 4px; margin: 8px 0;">
            <h4>Mock Analysis Results:</h4>
            <ul>
              <li><strong>Main Subject:</strong> Professional business setting</li>
              <li><strong>Mood:</strong> Professional, clean, modern</li>
              <li><strong>Colors:</strong> Blue, white, gray tones</li>
              <li><strong>Composition:</strong> Well-balanced, rule of thirds</li>
              <li><strong>Tags:</strong> business, professional, office, modern, clean</li>
            </ul>
          </div>
          <p><em>In production, this would be real AI analysis using GPT-4 Vision.</em></p>
        </div>
      `,
      excerpt: 'AI-powered image analysis with detailed visual insights',
      tags: ['image-analysis', 'ai-generated', 'mock'],
      suggestions: ['Configure OpenAI API key for real analysis', 'Review suggested tags', 'Use for content optimization'],
      confidence: 0.1
    };
  }

  private mockGenerateImage(prompt: string, style: string, size: string): ContentGenerationResponse {
    const placeholderUrl = `https://via.placeholder.com/${size.replace('x', 'x')}/cccccc/000000?text=${encodeURIComponent(prompt)}`;
    
    return {
      title: `Generated Image (Mock): ${prompt}`,
      content: `
        <div style="background: #f0f9ff; border: 1px solid #0ea5e9; border-radius: 8px; padding: 16px; margin: 16px 0;">
          <h3>AI Image Generation (Mock)</h3>
          <p><strong>Prompt:</strong> ${prompt}</p>
          <p><strong>Style:</strong> ${style}</p>
          <p><strong>Size:</strong> ${size}</p>
          <div style="text-align: center; margin: 16px 0;">
            <img src="${placeholderUrl}" alt="${prompt}" style="max-width: 100%; height: auto; border-radius: 8px;" />
          </div>
          <p><em>In production, this would be a real AI-generated image using DALL-E 3.</em></p>
        </div>
      `,
      excerpt: `AI-generated ${style} image based on: ${prompt}`,
      tags: ['ai-generated', 'dall-e', style, 'image', 'mock'],
      suggestions: ['Configure OpenAI API key for real generation', 'Download the image', 'Modify the prompt for variations'],
      confidence: 0.1
    };
  }

  get isServiceConfigured(): boolean {
    return this.isConfigured;
  }
}

export const openaiService = new OpenAIService();
